'use client';
import FormattedMessage from '@/components/formatted-message';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { forgotPasswordMutationFn } from '@/lib/api';
import z, { useZodForm } from '@/lib/zod';
import { ForgotPasswordSchema } from '@/schemas';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Loader, MailCheckIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ForgotPassword() {
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { mutate, isPending } = useMutation({
        mutationFn: forgotPasswordMutationFn,
    });

    const form = useZodForm(ForgotPasswordSchema, {
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = (values: z.infer<typeof ForgotPasswordSchema>) => {
        mutate(values, {
            onSuccess: ({ data }) => {
                setIsSubmitted(true);

                toast({
                    title: 'Request Sent',
                    description: <FormattedMessage message={data.message} />,
                });
            },
            onError: (error: Error & { errorCode?: string }) => {
                if (error.errorCode === 'RESOURCE_NOT_FOUND') {
                    setIsSubmitted(true);

                    toast({
                        title: 'Request Sent',
                        description: (
                            <FormattedMessage message={error.message} />
                        ),
                    });
                    return;
                }
                toast({
                    title: 'Not Allowed',
                    description: <FormattedMessage message={error.message} />,
                    variant: 'destructive',
                });
            },
        });
    };

    return (
        <main className='w-full min-h-[590px] h-full max-w-full flex items-center justify-center '>
            {!isSubmitted ? (
                <div className='w-full h-screen flex flex-col justify-center p-5 rounded-md'>
                    <Logo size='100px' />

                    <h1
                        className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-1.5 mt-8
        text-center'>
                        Reset password
                    </h1>
                    <p className='mb-6 text-center text-base dark:text-[#f1f7feb5] font-normal'>
                        Enter the primary email address associated with your
                        account and we’ll send you an email with instructions to
                        reset your password.
                    </p>
                    <Form {...form}>
                        <form
                            className='flex flex-col gap-6'
                            onSubmit={form.handleSubmit(onSubmit)}>
                            <div className='mb-0'>
                                <FormField
                                    control={form.control}
                                    name='email'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-muted-foreground text-sm'>
                                                Email
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder='user@example.com'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <Button
                                type='submit'
                                disabled={isPending}
                                className='w-full text-[15px] h-[40px] text-white font-semibold'>
                                {isPending && (
                                    <Loader className='animate-spin' />
                                )}
                                Send request
                            </Button>
                        </form>
                    </Form>

                    <Link to={'/login'}>
                        <Button
                            disabled={isPending}
                            variant={'outline'}
                            className='w-full my-4 text-[15px] h-[40px] text-white font-semibold'>
                            Back to login
                        </Button>
                    </Link>
                </div>
            ) : (
                <div className='w-full h-screen flex flex-col gap-2 items-center justify-center rounded-md'>
                    <div className='size-[48px]'>
                        <MailCheckIcon
                            size='48px'
                            className='animate-bounce'
                        />
                    </div>
                    <h2 className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold'>
                        Your request is sent
                    </h2>
                    <p className='mb-2 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                        If{' '}
                        <span className='text-foreground'>
                            {form.getValues().email}
                        </span>{' '}
                        is associated with an account,
                        <br /> we will send you a message.
                    </p>
                    <Link to='/login'>
                        <Button className='h-[40px]'>
                            Go to login
                            <ArrowRight />
                        </Button>
                    </Link>
                </div>
            )}
        </main>
    );
}
